import { Button, Tag, Tooltip } from 'antd'
import { CloseOutlined, ReadOutlined } from '@ant-design/icons'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { MBA_CHAPTER_CONTRACTS } from '@/lib/thesisFramework/chapterContracts'
import { gateStatusLabel, shortChapterTitle } from '@/lib/thesisFramework/gateLabels'
import { useThesisBriefStore } from '@/stores/thesisBriefStore'
import { useThesisWorkspaceStore } from '@/stores/thesisWorkspaceStore'
import classNames from 'classnames'

/** 正文右侧：六章契约 + 门控进度（默认收起；仅手动开合，切章不强制展开） */
export default function ChapterGateRail() {
  const { novelId } = useParams()
  const open = useThesisWorkspaceStore((s) => s.gateRailOpen)
  const setOpen = useThesisWorkspaceStore((s) => s.setGateRailOpen)
  const toggle = useThesisWorkspaceStore((s) => s.toggleGateRail)
  const activeChapterIndex = useThesisWorkspaceStore((s) => s.activeChapterIndex)

  const ensureBrief = useThesisBriefStore((s) => s.ensureBrief)
  const brief = useThesisBriefStore((s) =>
    novelId ? s.byNovelId[novelId] || s.getBrief(novelId) : null,
  )

  const ch = activeChapterIndex ?? 1
  const current = MBA_CHAPTER_CONTRACTS.find((c) => c.chapterIndex === ch)

  useEffect(() => {
    if (novelId) ensureBrief(novelId)
  }, [novelId, ensureBrief])

  const railShell =
    'border-[var(--tt-line)] bg-[var(--tt-surface)] text-[var(--tt-ink)]'

  if (!open) {
    return (
      <aside
        className={classNames(
          'hidden lg:flex flex-none w-10 border-l flex-col items-center gap-2 py-2 z-20',
          railShell,
        )}
      >
        <Tooltip title="展开契约与门控" placement="left">
          <Button
            type="text"
            shape="circle"
            icon={<ReadOutlined />}
            onClick={toggle}
            aria-label="展开契约与门控"
          />
        </Tooltip>
        <button
          type="button"
          className="flex-1 flex items-start justify-center pt-2 text-xs text-[var(--tt-muted)] hover:text-[var(--tt-accent)]"
          onClick={toggle}
          title="契约与门控"
        >
          <span style={{ writingMode: 'vertical-rl', letterSpacing: '0.14em' }}>
            第{ch}章
          </span>
        </button>
      </aside>
    )
  }

  const gate = brief?.chapterGates[String(ch)]

  return (
    <aside
      className={classNames(
        'hidden lg:flex flex-none w-[220px] shrink-0 border-l flex-col overflow-hidden z-20',
        railShell,
      )}
    >
      <header className="flex-none flex items-center justify-between gap-1 px-2 py-1.5 border-b border-[var(--tt-line)]">
        <strong className="text-sm truncate">契约与门控</strong>
        {/* placement=left：避免 tip 挤出视口右侧触发滚动条，导致正文左右跳动 */}
        <Tooltip title="收起" placement="left" mouseEnterDelay={0.2}>
          <Button
            type="text"
            shape="circle"
            size="small"
            className="shrink-0"
            icon={<CloseOutlined />}
            onClick={() => setOpen(false)}
            aria-label="收起契约与门控"
          />
        </Tooltip>
      </header>

      <div className="grow overflow-y-auto px-2 py-2 space-y-3 text-sm">
        {current ? (
          <section className="rounded border border-[var(--tt-line)] bg-[var(--tt-bg-mid)] p-2 space-y-1.5">
            <div className="flex items-start justify-between gap-1">
              <div className="min-w-0">
                <div className="text-[11px] text-[var(--tt-muted)]">当前章</div>
                <strong className="text-xs leading-snug">
                  第{ch}章 {shortChapterTitle(current.title)}
                </strong>
              </div>
              <Tag className="m-0 shrink-0">{gateStatusLabel(gate?.status)}</Tag>
            </div>
            <p className="text-[11px] leading-relaxed text-[var(--tt-muted)]">
              {current.roleInArgument}
            </p>
            <div>
              <div className="text-[11px] font-medium mb-0.5">须回答</div>
              <ul className="list-disc pl-3.5 text-[11px] space-y-0.5">
                {current.mustAnswer.slice(0, 3).map((q) => (
                  <li key={q}>{q}</li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[11px] font-medium mb-0.5">禁止</div>
              <ul className="list-disc pl-3.5 text-[11px] space-y-0.5 text-[var(--tt-status-warn-fg)]">
                {current.forbid.slice(0, 2).map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </div>
            {gate?.summary ? (
              <p className="text-[11px]">
                <span className="font-medium">摘要：</span>
                {gate.summary}
              </p>
            ) : null}
            {gate?.lastReview && !gate.lastReview.passed ? (
              <p className="text-[11px] text-[var(--tt-status-warn-fg)]">
                待改：{gate.lastReview.revisions.slice(0, 2).join('；')}
              </p>
            ) : null}
          </section>
        ) : null}

        <section>
          <div className="text-[11px] font-medium mb-1.5 text-[var(--tt-muted)]">六章进度</div>
          <ul className="space-y-1">
            {MBA_CHAPTER_CONTRACTS.map((c) => {
              const g = brief?.chapterGates[String(c.chapterIndex)]
              const active = c.chapterIndex === ch
              return (
                <li
                  key={c.chapterIndex}
                  className={classNames(
                    'rounded border border-[var(--tt-line)] px-1.5 py-1 text-[11px]',
                    active && 'border-[var(--tt-accent)] bg-[var(--tt-accent-soft)]',
                  )}
                >
                  <div className="flex items-center justify-between gap-1">
                    <span className={classNames('truncate', active && 'font-semibold')}>
                      {c.chapterIndex}. {shortChapterTitle(c.title)}
                    </span>
                    <Tag className="m-0 shrink-0 text-[10px] leading-none px-1">
                      {gateStatusLabel(g?.status)}
                    </Tag>
                  </div>
                </li>
              )
            })}
          </ul>
        </section>
      </div>
    </aside>
  )
}
